import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2, ClipboardList, Sparkles, Lock, UserPlus, X } from "lucide-react";

/**
 * Visitor-friendly survey dialog.
 * Flow:
 *  1. Visitor clicks a survey card → opens dialog
 *  2. Visitor steps through questions, picking an answer per question (state local)
 *  3. On "Valider mes réponses":
 *     - If logged in → submit immediately
 *     - Else → show auth gate (tabs: login / quick signup) → after auth, auto-submit
 *  4. Success screen with +10 points & CTA towards dashboard
 */
export default function VisitorSurveyDialog({ survey, open, onClose }) {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const isAuthed = !!user && user !== false;

  const [step, setStep] = useState(0);                  // current question index
  const [answers, setAnswers] = useState({});           // { [qIndex]: optIndex }
  const [mode, setMode] = useState("survey");           // 'survey' | 'auth' | 'done'
  const [authTab, setAuthTab] = useState("register");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!survey) return null;
  const totalQ = survey.questions?.length || 0;
  const allAnswered = totalQ > 0 && Object.keys(answers).length === totalQ;
  const progress = totalQ > 0 ? Math.round((Object.keys(answers).length / totalQ) * 100) : 0;

  const reset = () => {
    setStep(0); setAnswers({}); setMode("survey"); setBusy(false); setResult(null);
  };
  const handleClose = () => { reset(); onClose(); };

  const pick = (optIdx) => {
    setAnswers((a) => ({ ...a, [step]: optIdx }));
    if (step < totalQ - 1) setTimeout(() => setStep((s) => s + 1), 200);
  };

  const submitAnswers = async () => {
    if (!allAnswered) {
      toast.error("Répondez à toutes les questions avant de valider");
      return;
    }
    setBusy(true);
    try {
      const payload = { answers: survey.questions.map((_, i) => answers[i]) };
      const { data } = await api.post(`/surveys/${survey.id}/respond`, payload);
      setResult(data);
      setMode("done");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const tryValidate = () => {
    if (!allAnswered) {
      toast.error("Répondez à toutes les questions avant de valider");
      return;
    }
    if (isAuthed) {
      submitAnswers();
    } else {
      setMode("auth");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-xl bg-cream-50 border-cream-200 rounded-2xl p-0 overflow-hidden" data-testid="visitor-survey-dialog">
        {/* Header band */}
        <div className="bg-gradient-to-br from-plum-700 via-plum-800 to-pink-600 text-white px-5 py-5">
          <DialogHeader className="text-left">
            <div className="flex items-center justify-between mb-2">
              <span className="phenix-pill bg-white/15 text-white border-white/20 text-[10px] uppercase tracking-wider">
                <ClipboardList size={11} strokeWidth={2.5} /> Sondage
              </span>
              <button onClick={handleClose} className="text-white/70 hover:text-white" aria-label="Fermer" data-testid="survey-close-btn">
                <X size={18} />
              </button>
            </div>
            <DialogTitle className="font-display font-extrabold text-xl sm:text-2xl text-white tracking-tight">
              {survey.title}
            </DialogTitle>
            {survey.description && (
              <p className="text-[13px] text-cream-50/80 mt-1 leading-snug">{survey.description}</p>
            )}
          </DialogHeader>

          {mode !== "done" && (
            <div className="mt-4">
              <div className="flex justify-between text-[10px] uppercase tracking-widest text-cream-50/65 mb-1.5">
                <span>{mode === "auth" ? "Validation" : `Question ${Math.min(step + 1, totalQ)} sur ${totalQ}`}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 bg-white/15 rounded-full overflow-hidden">
                <div className="h-full bg-sun-300 transition-all duration-300" style={{ width: `${mode === "auth" ? 100 : progress}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-5 py-5 sm:px-6">
          {mode === "survey" && (
            <SurveyStep
              question={survey.questions[step]}
              currentAnswer={answers[step]}
              onPick={pick}
              dataTestidPrefix={`survey-${survey.id}-q${step}`}
            />
          )}

          {mode === "auth" && (
            <AuthGate
              authTab={authTab}
              setAuthTab={setAuthTab}
              login={login}
              register={register}
              onAuthed={submitAnswers}
              busy={busy}
              setBusy={setBusy}
            />
          )}

          {mode === "done" && (
            <DoneScreen
              result={result}
              onClose={handleClose}
              onSeeMyDashboard={() => { handleClose(); navigate("/habitant"); }}
            />
          )}
        </div>

        {/* Footer actions */}
        {mode === "survey" && (
          <div className="px-5 sm:px-6 pb-5 flex items-center justify-between gap-3 border-t border-cream-200 pt-4">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="text-[13px] font-semibold text-plum-700/70 hover:text-plum-700 disabled:opacity-30 inline-flex items-center gap-1.5"
              data-testid="survey-prev-btn"
            >
              <ArrowLeft size={14} /> Précédente
            </button>

            {step < totalQ - 1 ? (
              <Button
                onClick={() => setStep((s) => Math.min(totalQ - 1, s + 1))}
                disabled={answers[step] === undefined}
                className="btn-pill bg-plum-700 hover:bg-plum-800 text-white h-11 px-5"
                data-testid="survey-next-btn"
              >
                Suivante <ArrowRight size={14} />
              </Button>
            ) : (
              <Button
                onClick={tryValidate}
                disabled={!allAnswered || busy}
                className="btn-pill bg-sun-400 hover:bg-sun-500 text-plum-900 h-11 px-5 font-bold"
                data-testid="survey-validate-btn"
              >
                {isAuthed ? "Valider mes réponses" : "Continuer pour valider"} <ArrowRight size={14} />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SurveyStep({ question, currentAnswer, onPick, dataTestidPrefix }) {
  return (
    <div className="space-y-3">
      <h3 className="font-display font-bold text-lg text-plum-900 leading-snug">{question?.question}</h3>
      <div className="space-y-2">
        {question?.options?.map((opt, oi) => {
          const active = currentAnswer === oi;
          return (
            <button
              key={oi}
              onClick={() => onPick(oi)}
              data-testid={`${dataTestidPrefix}-opt${oi}`}
              className={`w-full text-left px-4 py-3.5 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                active
                  ? "bg-plum-700 text-white border-plum-700 shadow-md shadow-plum-700/25"
                  : "bg-white border-cream-200 hover:border-plum-300 text-plum-900"
              }`}
            >
              <span className={`w-7 h-7 rounded-full grid place-items-center text-[12px] font-bold shrink-0 ${
                active ? "bg-sun-300 text-plum-900" : "bg-plum-50 text-plum-700"
              }`}>
                {String.fromCharCode(65 + oi)}
              </span>
              <span className="text-[14px] font-medium flex-1">{opt}</span>
              {active && <CheckCircle2 size={18} className="text-sun-300 shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AuthGate({ authTab, setAuthTab, login, register, onAuthed, busy, setBusy }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pseudo, setPseudo] = useState("");
  const [name, setName] = useState("");

  const doLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) return toast.error("Email et mot de passe requis");
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Connexion réussie ✓");
      await onAuthed();
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };

  const doRegister = async (e) => {
    e?.preventDefault();
    if (!email || !password || !pseudo) return toast.error("Pseudo, email et mot de passe requis");
    setBusy(true);
    try {
      await register({
        email, password,
        name: name || pseudo,
        pseudo,
        role: "habitant",
      });
      toast.success("Compte créé ✓");
      await onAuthed();
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="visitor-survey-auth-gate">
      <div className="flex items-start gap-3 p-3 rounded-xl bg-sun-50 border border-sun-200">
        <Sparkles size={18} className="text-sun-600 mt-0.5 shrink-0" strokeWidth={2.2} />
        <div>
          <div className="font-display font-bold text-sm text-plum-900">Plus qu&apos;une étape !</div>
          <p className="text-[12px] text-plum-700/75 leading-snug">
            Connectez-vous ou créez votre compte (30 secondes) pour valider vos réponses et gagner <strong className="text-pink-600">+10 points</strong>.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-2 bg-cream-100 rounded-full p-1 gap-1">
        <button
          onClick={() => setAuthTab("register")}
          data-testid="auth-tab-register"
          className={`h-10 rounded-full text-[13px] font-bold inline-flex items-center justify-center gap-1.5 transition-all ${
            authTab === "register" ? "bg-plum-700 text-white shadow-sm" : "text-plum-700/75 hover:text-plum-700"
          }`}
        >
          <UserPlus size={13} /> Création rapide
        </button>
        <button
          onClick={() => setAuthTab("login")}
          data-testid="auth-tab-login"
          className={`h-10 rounded-full text-[13px] font-bold inline-flex items-center justify-center gap-1.5 transition-all ${
            authTab === "login" ? "bg-plum-700 text-white shadow-sm" : "text-plum-700/75 hover:text-plum-700"
          }`}
        >
          <Lock size={13} /> J&apos;ai déjà un compte
        </button>
      </div>

      {authTab === "register" ? (
        <form onSubmit={doRegister} className="space-y-3" data-testid="quick-register-form">
          <div className="grid sm:grid-cols-2 gap-2.5">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-plum-700/70">Pseudo</Label>
              <Input data-testid="register-pseudo" value={pseudo} onChange={(e) => setPseudo(e.target.value)} placeholder="ex: jeanne13" className="rounded-xl h-11 bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-plum-700/70">Prénom (optionnel)</Label>
              <Input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jeanne" className="rounded-xl h-11 bg-white" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-plum-700/70">Email</Label>
            <Input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@email.fr" className="rounded-xl h-11 bg-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-plum-700/70">Mot de passe</Label>
            <Input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6 caractères minimum" className="rounded-xl h-11 bg-white" />
          </div>
          <Button type="submit" disabled={busy} data-testid="register-submit" className="btn-pill bg-plum-700 hover:bg-plum-800 text-white w-full h-12 mt-2">
            Créer mon compte &amp; valider <ArrowRight size={14} />
          </Button>
        </form>
      ) : (
        <form onSubmit={doLogin} className="space-y-3" data-testid="quick-login-form">
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-plum-700/70">Email</Label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@email.fr" className="rounded-xl h-11 bg-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-plum-700/70">Mot de passe</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl h-11 bg-white" />
          </div>
          <Button type="submit" disabled={busy} data-testid="login-submit" className="btn-pill bg-plum-700 hover:bg-plum-800 text-white w-full h-12 mt-2">
            Se connecter &amp; valider <ArrowRight size={14} />
          </Button>
        </form>
      )}

      <div className="text-[11px] text-plum-700/55 text-center">
        En continuant, vous acceptez nos {" "}
        <Link to="/mentions-legales" className="underline">mentions légales</Link>{" "}et notre{" "}
        <Link to="/confidentialite" className="underline">politique de confidentialité</Link>.
      </div>
    </div>
  );
}

function DoneScreen({ result, onClose, onSeeMyDashboard }) {
  return (
    <div className="text-center py-3" data-testid="visitor-survey-done">
      <div className="w-16 h-16 rounded-full bg-mint-100 text-mint-600 grid place-items-center mx-auto mb-3">
        <CheckCircle2 size={32} strokeWidth={2.2} />
      </div>
      <h3 className="font-display font-extrabold text-xl text-plum-900 mb-1">Merci pour votre voix !</h3>
      <p className="text-[14px] text-plum-700/75 max-w-sm mx-auto leading-snug">
        Vos réponses ont été enregistrées. Vous gagnez <strong className="text-pink-600">+{result?.points_earned ?? 10} points</strong> sur votre compte Istres &amp; Moi.
      </p>
      <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
        <Button onClick={onSeeMyDashboard} className="btn-pill bg-plum-700 hover:bg-plum-800 text-white h-11 px-5" data-testid="goto-dashboard-btn">
          Voir mon espace <ArrowRight size={14} />
        </Button>
        <Button onClick={onClose} variant="ghost" className="rounded-full h-11 px-5 text-plum-700">
          Continuer à explorer
        </Button>
      </div>
    </div>
  );
}
